/* Strings for the figure island.

   The same three exports `src/components/cd/strings-context.jsx` has, so
   figures.jsx is copied across unchanged and keeps working. What differs is where the copy comes from and what happens when
   there is none.

   On the Astro side the default was the English table, imported from
   src/strings.ts, because that file was in the same build. Here the copy
   arrives from PHP as JSON on window.cdFigure, printed next to the script tag
   by wp_add_inline_script(), so there is no table to import and no second copy
   to drift. A missing key renders its own path -- `figure.revision` -- which is
   the same trade-off text() makes in PHP: visibly wrong beats silently empty.

   Do not add an English fallback table here. Two copies of the copy is the
   thing the strings test exists to prevent, and this file is out of its reach. */
import React from "react";

const StringsContext = React.createContext(null);

/** The copy PHP printed, or an empty table if the script tag went missing. */
export function injectedStrings() {
	const data = typeof window !== "undefined" ? window.cdFigure : null;
	return data && typeof data.strings === "object" ? data.strings : {};
}

/** A table that answers every lookup with the key's own path. */
function pathEcho(group) {
	return new Proxy(
		{},
		{
			get: (_target, key) => (typeof key === "string" ? `${group}.${key}` : undefined),
		},
	);
}

export function StringsProvider({ value, children }) {
	return (
		<StringsContext.Provider value={value || injectedStrings()}>
			{children}
		</StringsContext.Provider>
	);
}

/** The whole strings object, never null and never missing a group. */
export function useStrings() {
	const table = React.useContext(StringsContext) || injectedStrings();
	return {
		...table,
		figure: table.figure ?? pathEcho("figure"),
		form: table.form ?? pathEcho("form"),
	};
}

/** Replace `%x` placeholders, as fill() does in src/strings.ts. */
export function fill(template, values = {}) {
	return String(template).replace(/%([a-z])/g, (whole, key) => values[key] ?? whole);
}
